import { useCallback, useContext } from 'react';
import { B2BContactFormProps } from '../field-types';
import { AllContactMeFormsProps } from '../contact-form';
import { ButtonType } from '@/isomorphic/components/typography/button/button';
import { useForm } from '@/hooks/use-form';
import { useGtm } from '@/hooks/use-gtm';
import { GenericEvent, useDataLayer } from '@/hooks/use-data-layer';
import { isCompanyEmail, validators } from '@/hooks/use-validation';
import { FormRenderer } from '../shared/form-renderer';
import { FormSettings, FormValues, SubmitResult } from '../shared/types';
import { AppContext } from '@/contexts/app-context';
import { SubmitContactPayload, useCompanyCreditCheck } from '../../company-credit-check/use-company-credit-check';
import { useGlobalQueries } from '@/hooks/use-global-queries';

type B2BFormSettings = {
    formId?: string;
    successTitle?: string;
    successMessage?: string;
    darkMode?: boolean;
    buttonType?: ButtonType;
    generalSubmitErrorMessage?: string;
    // B2B specific
    contactMethod?: 'email' | 'api';
    onlyConfirmLimitedCompanyMode?: boolean;
    conditionalBlockedTitle?: string;
    conditionalBlockedMessage?: string;
    // GTM
    gtmSendFormEvent?: boolean;
    gtmEventUseCustomProduct?: boolean;
    gtmProduct?: string;
    gtmProductVariant?: string;
};

export type AllB2BContactFormProps = B2BFormSettings & B2BContactFormProps;

/**
 * Fields with a predefined API mapping. These keys are locked in the editor and
 * always sent under the value here regardless of any admin-entered API label.
 * `companyRegistrationNumber` is intentionally excluded - it is sent as the
 * `orgNumber` URL path segment, not in the body.
 */
const LOCKED_API_LABELS: Record<string, string> = {
    email: 'email',
    phone: 'telephone',
    message: 'message',
    annualRevenue: 'annualRevenue',
    loanAmount: 'loanAmount',
};

/** Keys that are always present in the payload and must not be overwritten by a custom field. */
const RESERVED_PAYLOAD_KEYS = new Set(['email', 'telephone', 'source', 'orgNumber']);

/** Normalise an admin-entered API label into a camelCase JSON key (defensive; the editor already camelCases). */
const toCamelCaseKey = (value: string): string => {
    const words = value
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (words.length === 0) {
        return '';
    }

    return words
        .map((word, index) => {
            // Preserve casing within the word; only adjust the first character so
            // admin-entered camelCase (e.g. "costCenter") is kept intact.
            return index === 0 ? `${word.charAt(0).toLowerCase()}${word.slice(1)}` : `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
        })
        .join('')
        .replace(/^[0-9]+/, '');
};

/** Map a form field name to the `<field>ApiLabel` prop key. */
const apiLabelPropName = (fieldName: string): keyof B2BContactFormProps => `${fieldName}ApiLabel` as keyof B2BContactFormProps;

/**
 * Check if org number indicates a Limited Company based on locale.
 * Currently supports Swedish Aktiebolag (556/559).
 * TODO: Add support for Norwegian AS, Danish A/S, Finnish Oy
 */
const isLimitedCompany = (orgNumber: string, locale: string): boolean => {
    const cleaned = orgNumber.replace(/\D/g, '');

    switch (locale) {
        case 'sv-se':
            // Swedish Aktiebolag starts with 556 or 559
            return cleaned.startsWith('556') || cleaned.startsWith('559');
        // TODO: Add Nordic support
        // case 'nb-no':
        //     return isNorwegianAS(cleaned);
        // case 'da-dk':
        //     return isDanishAS(cleaned);
        // case 'fi-fi':
        //     return isFinnishOy(cleaned);
        default:
            return true; // No validation for unsupported locales
    }
};

export const B2BContactForm = (props: AllB2BContactFormProps) => {
    const { locale } = useContext(AppContext);
    const { parsePropsToFieldObjects } = useForm();
    const { sendFormSubmitEvent } = useGtm();
    const { dataLayerPush } = useDataLayer();
    const { submitContact } = useCompanyCreditCheck();
    const { pageQuery } = useGlobalQueries();

    const contactMethod = props.contactMethod || 'email';

    const parsedFields = parsePropsToFieldObjects(props as unknown as AllContactMeFormsProps);

    // Get dropdown field to check for "not allowed" values (fieldName is the label)
    const dropdownField = parsedFields.find(f => f.type === 'select' && f.list);
    const dropdownFieldName = dropdownField?.fieldName;
    const dropdownList = dropdownField?.list as { name: string; value: string; notAllowed?: boolean }[] | undefined;

    const formSettings: FormSettings = {
        formId: props.formId,
        successTitle: props.successTitle,
        successMessage: props.successMessage,
        generalSubmitErrorMessage: props.generalSubmitErrorMessage,
        darkMode: props?.darkMode || false,
        buttonType: props?.buttonType || 'primary',
        gtmSendFormEvent: props?.gtmSendFormEvent,
        gtmEventUseCustomProduct: props?.gtmEventUseCustomProduct,
        gtmProduct: props?.gtmProduct || '',
        gtmProductVariant: props?.gtmProductVariant || '',
    };

    const handleSuccess = () => {
        // Legacy GTM event
        sendFormSubmitEvent();

        if (!formSettings.gtmSendFormEvent) {
            return;
        }

        const event: GenericEvent = {
            event: 'FormSubmit',
        };

        if (formSettings.gtmEventUseCustomProduct && formSettings.gtmProduct) {
            event.Product = formSettings.gtmProduct;
            if (formSettings.gtmProductVariant) {
                event.ProductVariant = formSettings.gtmProductVariant;
            }
            dataLayerPush(event, { businessVertical: 'B2B' });
            return;
        }

        dataLayerPush(event, { usePageProductData: true, businessVertical: 'B2B' });
    };

    const b2bSettings = {
        onlyConfirmLimitedCompanyMode: props?.onlyConfirmLimitedCompanyMode || false,
        conditionalBlockedTitle: props?.conditionalBlockedTitle || '',
        conditionalBlockedMessage: props?.conditionalBlockedMessage || '',
    };

    /**
     * Override email field validator when org number is Aktiebolag
     */
    const getFieldValidator = (fieldName: string, formValues: FormValues) => {
        if (fieldName === 'loanAmount') {
            // Parse the admin-configured minimum (tolerate whitespace / thousand separators).
            const rawMin = String(props.loanAmountMin ?? '').replace(/[\s.,]/g, '');
            const minAmount = /^\d+$/.test(rawMin) ? parseInt(rawMin, 10) : 0;

            // Two distinct failures need two distinct messages, but the Field component
            // renders a single error message. Pick the message based on which rule the
            // current value fails: a non-number uses the generic error, a valid number
            // below the minimum uses the dedicated min error.
            const currentValue = String(formValues.loanAmount ?? '');
            const isValidNumber = validators.loanAmount(currentValue);
            const errorMessage = !isValidNumber
                ? props.loanAmountErrorMessage || undefined
                : props.loanAmountMinErrorMessage || props.loanAmountErrorMessage || undefined;

            return {
                validator: (value: string) => {
                    // Base rule: positive integer. Empty optional values are skipped by the renderer.
                    if (!validators.loanAmount(value)) {
                        return false;
                    }
                    if (minAmount > 0) {
                        const normalized = value.replace(/[\s.,]/g, '');
                        return parseInt(normalized, 10) >= minAmount;
                    }
                    return true;
                },
                errorMessage,
            };
        }

        if (fieldName === 'email') {
            const orgNumber = (formValues.companyRegistrationNumber as string) || '';
            const { onlyConfirmLimitedCompanyMode } = b2bSettings;

            if (onlyConfirmLimitedCompanyMode && orgNumber && isLimitedCompany(orgNumber, locale)) {
                // When Aktiebolag, require company email
                return {
                    validator: (value: string) => validators.email(value) && isCompanyEmail(value),

                    // Hard coded message hmm
                    errorMessage: 'Vänligen ange en godkänd företagsmail',
                };
            }
        }
        return undefined; // Use default validator
    };

    /**
     * Determine submission result (confirmed/blocked) based on B2B rules
     */
    const checkB2BConditions = (formValues: FormValues): SubmitResult => {
        const { onlyConfirmLimitedCompanyMode } = b2bSettings;

        if (!onlyConfirmLimitedCompanyMode) {
            return { confirmed: true };
        }

        const orgNumber = (formValues.companyRegistrationNumber as string) || '';
        const email = (formValues.email as string) || '';
        const dropdownValue = dropdownFieldName ? (formValues[dropdownFieldName] as string) || '' : '';

        if (orgNumber && !isLimitedCompany(orgNumber, locale)) {
            return { confirmed: false };
        }

        if (email && !isCompanyEmail(email)) {
            return { confirmed: false };
        }

        if (dropdownValue && dropdownList) {
            const selectedOption = dropdownList.find(opt => opt.value === dropdownValue);
            if (selectedOption?.notAllowed) {
                return { confirmed: false };
            }
        }

        return { confirmed: true };
    };

    const getSuccessContent = (submitResult: SubmitResult) => {
        if (b2bSettings.onlyConfirmLimitedCompanyMode && !submitResult.confirmed) {
            return {
                title: b2bSettings.conditionalBlockedTitle || '',
                message: b2bSettings.conditionalBlockedMessage || formSettings.successMessage || '',
            };
        }
        return {
            title: formSettings.successTitle || '',
            message: formSettings.successMessage || '',
        };
    };

    /**
     * Submit form data to the credit-indication API instead of sending an email.
     * Calls the credit-indication contact endpoint with orgNumber in the URL path.
     */
    const submitViaApi = useCallback(
        async (formValues: FormValues, token: string) => {
            const orgNumber = String(formValues.companyRegistrationNumber ?? '').replace(/[^0-9]/g, '');
            if (!orgNumber) {
                throw new Error('Missing orgNumber');
            }

            const pageTitle = pageQuery.data?.page_title || pageQuery.data?.post_title || '';
            const sourceFormName = 'B2B Contact Form';
            const source = pageTitle ? `${pageTitle} - ${sourceFormName}` : sourceFormName;

            // Required/locked fields are always sent under their fixed keys.
            const contactData: SubmitContactPayload = {
                email: String(formValues.email ?? ''),
                telephone: String(formValues.phone ?? ''),
                source,
            };

            // Always send every enabled field, mapped to its API key.
            // Resolution order: explicit (camelCased) API label -> predefined mapping -> camelCased field name.
            // The API label is never empty, so empty `<field>ApiLabel` attributes (e.g. blocks where the
            // admin never edited the input, or blocks saved before the feature existed) still resolve to the
            // locked mapping or a generated default rather than dropping the field.
            // companyRegistrationNumber/email/phone are handled above (orgNumber path + locked body keys).
            const handledFields = new Set(['companyRegistrationNumber', 'email', 'phone']);

            parsedFields.forEach(parsedField => {
                const fieldName = parsedField.fieldName;
                if (!fieldName || handledFields.has(fieldName)) {
                    return;
                }

                const rawValue = formValues[fieldName];
                // Skip empty/falsy values so we never emit empty JSON keys/values.
                if (rawValue === undefined || rawValue === null || rawValue === '' || rawValue === false) {
                    return;
                }

                // The dropdown field is keyed by its admin label, not "dropdown",
                // so its API label lives under the dedicated `dropdownApiLabel` prop.
                const isDropdown = parsedField.type === 'select' && Boolean(parsedField.list);
                const customLabel = (isDropdown ? props.dropdownApiLabel : (props[apiLabelPropName(fieldName)] as string | undefined)) as
                    | string
                    | undefined;

                // The API label is always sent: explicit (camelCased) label, else the
                // predefined mapping, else a camelCased default derived from the field name.
                const resolvedKey = (customLabel && toCamelCaseKey(customLabel)) || LOCKED_API_LABELS[fieldName] || toCamelCaseKey(fieldName);

                // Never clobber a reserved key that is always part of the payload.
                if (!resolvedKey || RESERVED_PAYLOAD_KEYS.has(resolvedKey)) {
                    return;
                }

                // loanAmount is typed as an integer in the API spec; send it as a number.
                if (fieldName === 'loanAmount') {
                    const amount = parseInt(String(rawValue).replace(/[\s.,]/g, ''), 10);
                    if (!Number.isNaN(amount)) {
                        contactData[resolvedKey] = amount;
                    }
                    return;
                }

                contactData[resolvedKey] = String(rawValue);
            });

            const result = await submitContact(orgNumber, contactData, token);

            if (result !== 'OK') {
                throw new Error(result);
            }
        },
        [pageQuery.data, submitContact, parsedFields, props]
    );

    const isApiMode = contactMethod === 'api';

    return (
        <FormRenderer
            parsedFields={parsedFields}
            formSettings={formSettings}
            onBeforeSubmit={checkB2BConditions}
            onSuccess={handleSuccess}
            getSuccessContent={getSuccessContent}
            getFieldValidator={getFieldValidator}
            dataComponent="resurs/b2b-contact-form"
            submissionRoute="/api/b2b-form"
            includeB2bConfirmed={!isApiMode}
            {...(isApiMode ? { onSubmit: submitViaApi } : {})}
        />
    );
};
